import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import { AppBar, Box, Toolbar, Typography } from '@mui/material';
import { useTranslate } from 'react-admin';
import { EmptyState } from './EmptyState';

/**
 * Rendered by {@link import('./AccessGatePage').AccessGatePage} when the
 * access provider resolves `{ kind: 'unavailable' }` — the access check
 * itself could not be completed (a configuration or infrastructure problem,
 * such as the CSP defect wave B6 fixed: the identity provider's silent
 * session check never got a response back). This is deliberately a
 * **third**, distinct component from both {@link
 * import('./NoSessionPage').NoSessionPage} (no session at all) and {@link
 * import('./NotEntitledPage').NotEntitledPage} (a real session without
 * access to this application): none of the three may ever be collapsed into
 * one another, because they are three different facts an operator needs to
 * act on differently.
 *
 * Same discipline as the other two: no field, no submit button, no
 * credential of any kind, and never a login form. Additionally, this screen
 * never redirects anywhere — the failure it represents may itself be why a
 * redirect would never complete, and a redirect loop on top of a
 * misconfiguration is strictly worse than a static, readable message.
 */
export function AccessUnavailablePage() {
  const translate = useTranslate();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        position="static"
        color="inherit"
        elevation={0}
        sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Toolbar>
          <Typography
            component="span"
            sx={{ whiteSpace: 'nowrap', fontSize: '1.05rem', letterSpacing: '-0.3px', lineHeight: 1 }}
          >
            <Box component="span" sx={{ color: (theme) => theme.custom.wordmarkBrandColor, fontWeight: 800 }}>
              {translate('pay.appBar.wordmarkBrand')}
            </Box>
            <Box
              component="span"
              sx={{ color: (theme) => theme.custom.wordmarkProductColor, fontWeight: 500, ml: 0.75 }}
            >
              {translate('pay.appBar.wordmarkProduct')}
            </Box>
          </Typography>
        </Toolbar>
      </AppBar>
      <EmptyState
        icon={<ReportProblemOutlinedIcon fontSize="inherit" />}
        title={translate('pay.accessUnavailable.title')}
        body={translate('pay.accessUnavailable.body')}
      />
    </Box>
  );
}
